/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/StatusPoliciesPanel" />
import React = require("react");
import { GitStatusContext } from "Repos/Common/Contracts/Git";
import { IPolicy, IStatusPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Accordion } from "VSSUI/Accordion";
import { Checkbox } from "VSSUI/Checkbox";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IIdentity, IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { getSafeId } from "VSSUI/Util";
import { renderTooltipWithBreaks } from "./Util";

export interface IStatusPoliciesPanelProps {
    onDismiss: () => void;
    materializeIdentity: (identity: IIdentity) => Promise<IIdentity>;
    policy: IStatusPolicy;
    recentStatuses: IObservableValue<GitStatusContext[] | undefined>;
    splitStatus: (status: string) => string[];
    supportServicePrincipals: boolean;
    save: (policy: IPolicy, handleError: (error: any) => void) => void;
}

const enum authorizedOrganizationValues {
    any = "any",
    specific = "specific"
}

const requiredId = "required-status";
const optionalId = "optional-status";

export class StatusPoliciesPanel extends VssComponent<IStatusPoliciesPanelProps> {
    private author: IObservableValue<IIdentity | undefined>;
    private authorizedOrganization: IObservableValue<string>;
    private disabled: boolean;
    private displayNameValue: IObservableValue<string>;
    private formErrorMessage: IObservableValue<string | undefined>;
    private failureMessage: IObservableValue<string | undefined>;
    private isBusy: IObservableValue<boolean>;
    private isCollapsed: IObservableValue<boolean>;
    private isToggled: IObservableValue<boolean>;
    private isValid: IObservableValue<boolean>;
    private pathFilterValue: IObservableValue<string>;
    private peoplePickerProvider: PeoplePickerProvider;
    private policy: IStatusPolicy;
    private selectedResetConditions: IObservableValue<boolean>;
    private selectedStatus: IObservableValue<string>;
    private selectedPolicyRequirement: IObservableValue<string>;
    private selectedPolicyApplicability: IObservableValue<string>;
    private title: string;
    private renderSeparately: IObservableValue<boolean>;
    private statusName: IObservableValue<string>;
    private statusGenre: IObservableValue<string>;

    public constructor(props: IStatusPoliciesPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.peoplePickerProvider = new PeoplePickerProvider(context.pageContext, {
            identityTypes: this.props.supportServicePrincipals ? ["user", "group", "servicePrincipal"] : ["user", "group"],
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            operationScopes: ["ims", "source"]
        });

        this.policy = { ...this.props.policy };
        this.policy.complexSettings = { ...this.policy.complexSettings! };

        this.isBusy = new ObservableValue<boolean>(false);
        if (this.policy.isEnterpriseManaged || this.policy.isInherited || this.policy.disabled) {
            this.title = Resources.StatusPolicyView;
            this.disabled = true;
        } else {
            this.disabled = false;

            if (this.policy.id === -1) {
                this.title = Resources.StatusPolicyAdd;
            } else {
                this.title = Resources.StatusPolicyEdit;
            }
        }
        const settings = this.policy.complexSettings;

        this.selectedResetConditions = new ObservableValue<boolean>(settings.invalidateOnSourceUpdate);
        this.selectedStatus = new ObservableValue<string>(settings.status || "");
        this.pathFilterValue = new ObservableValue<string>(settings.filenamePatterns || "");
        this.displayNameValue = new ObservableValue<string>(settings.defaultDisplayName || "");
        this.selectedPolicyRequirement = new ObservableValue<string>(settings.required ? requiredId : optionalId);
        this.selectedPolicyApplicability = new ObservableValue<string>(settings.policyApplicability ? "conditional" : "default");

        this.failureMessage = new ObservableValue<string | undefined>(undefined);
        this.formErrorMessage = new ObservableValue<string | undefined>(undefined);

        this.isToggled = new ObservableValue<boolean>(this.policy.value);
        this.author = new ObservableValue<IIdentity | undefined>(settings.author);
        this.isValid = new ObservableValue(false);
        this.isCollapsed = new ObservableValue(this.isCollapsedByDefault());
        this.authorizedOrganization = new ObservableValue<string>(
            !!settings.authorId.length ? authorizedOrganizationValues.specific : authorizedOrganizationValues.any
        );

        this.author.subscribe(this.checkValid);
        this.selectedStatus.subscribe(this.checkValid);

        this.renderSeparately = new ObservableValue<boolean>(false);
        this.statusName = new ObservableValue<string>(settings.statusName || "");
        this.statusGenre = new ObservableValue<string>(settings.statusGenre || "");

        this.checkValid();
    }

    public componentWillUnmount = () => {
        this.author.unsubscribe(this.checkValid);
        this.selectedStatus.unsubscribe(this.checkValid);
    };

    public render = (): JSX.Element => {
        const accordionSections = [
            {
                isCollapsed: this.isCollapsed,
                key: "advanced",
                onRenderContent: () => (
                    <div className="flex-column rhythm-vertical-16 flex-grow">
                        <FormItem label={Resources.AuthorizedOrganization}>
                            <RadioButtonGroup
                                onSelect={id => {
                                    if (id === authorizedOrganizationValues.any) {
                                        this.policy.complexSettings!.author = undefined;
                                        this.policy.complexSettings!.authorId = "";
                                        this.author.value = undefined;
                                    }
                                    this.authorizedOrganization.value = id;
                                }}
                                selectedButtonId={this.authorizedOrganization}
                                className="flex-start policy-status-radio-button-group"
                            >
                                <RadioButton
                                    id={authorizedOrganizationValues.any}
                                    key={authorizedOrganizationValues.any}
                                    disabled={this.disabled}
                                    text={Resources.AuthorizedOrganizationAny}
                                />
                                <RadioButton
                                    id={authorizedOrganizationValues.specific}
                                    key={authorizedOrganizationValues.specific}
                                    disabled={this.disabled}
                                    text={Resources.AuthorizedOrganizationSpecific}
                                />
                            </RadioButtonGroup>
                            <div className="policy-status-picker">
                                <Observer authorizedOrganization={this.authorizedOrganization}>
                                    {() => {
                                        return (
                                            <IdentityPickerDropdown
                                                value={this.author}
                                                pickerProvider={this.peoplePickerProvider}
                                                onChange={identity => {
                                                    this.author.value = identity;
                                                    this.policy.complexSettings!.author = identity;
                                                    this.policy.complexSettings!.authorId = identity ? identity.localId || "" : "";
                                                }}
                                                disabled={this.authorizedOrganization.value === authorizedOrganizationValues.any}
                                                key={this.policy.complexSettings!.authorId} /* need to clear if other raido button selected */
                                            />
                                        );
                                    }}
                                </Observer>
                            </div>
                        </FormItem>
                        <FormItem label={Resources.ResetConditions}>
                            <Checkbox
                                checked={this.selectedResetConditions}
                                onChange={(event, value) => {
                                    this.selectedResetConditions.value = value;
                                    this.policy.complexSettings!.invalidateOnSourceUpdate = value;
                                }}
                                disabled={this.disabled}
                                label={Resources.ResetConditionsCheckboxText}
                            />
                        </FormItem>
                        <FormItem label={Resources.PathFilterOptional}>
                            <div className="flex-row flex-center">
                                <TextField
                                    value={this.pathFilterValue}
                                    onChange={(event, value) => {
                                        this.pathFilterValue.value = value;
                                        this.policy.complexSettings!.filenamePatterns = value;
                                    }}
                                    width={TextFieldWidth.auto}
                                    disabled={this.disabled}
                                    className="icon-margin flex-grow"
                                    containerClassName="flex-grow"
                                    inputClassName="flex-grow"
                                />
                                <Icon
                                    ariaLabel={Resources.PathFilterInfo}
                                    iconName="Info"
                                    tooltipProps={{
                                        renderContent: () => renderTooltipWithBreaks(Resources.PathFilterInfo)
                                    }}
                                />
                            </div>
                        </FormItem>
                        <FormItem label={Resources.PolicyApplicability}>
                            <RadioButtonGroup
                                onSelect={id => {
                                    this.selectedPolicyApplicability.value = id;
                                    this.policy.complexSettings!.policyApplicability = id == "conditional" ? true : false;
                                }}
                                selectedButtonId={this.selectedPolicyApplicability}
                                className="flex-start policy-status-radio-button-group"
                            >
                                <RadioButton id={"default"} ariaLabelledBy={"required-rblabel"} key={"default"} disabled={this.disabled}>
                                    <div
                                        className="policy-status-radio-button-custom-label bolt-radio-button-label"
                                        id={getSafeId("default-rblabel")}
                                    >
                                        {Resources.ApplyByDefault}
                                        <span className="body-s secondary-text">{Resources.ApplyByDefaultDesc}</span>
                                    </div>
                                </RadioButton>
                                <RadioButton
                                    id={"conditional"}
                                    ariaLabelledBy={"conditional-rblabel"}
                                    key={"conditional"}
                                    className="margin-top-16"
                                    disabled={this.disabled}
                                >
                                    <div
                                        className="policy-status-radio-button-custom-label bolt-radio-button-label"
                                        id={getSafeId("conditional-rblabel")}
                                    >
                                        {Resources.Conditional}
                                        <span className="body-s secondary-text">{Resources.ConditionalDesc}</span>
                                    </div>
                                </RadioButton>
                            </RadioButtonGroup>
                        </FormItem>
                        <FormItem label={Resources.DefaultDisplayName}>
                            <TextField
                                value={this.displayNameValue}
                                onChange={(event, value) => {
                                    this.displayNameValue.value = value;
                                    this.policy.complexSettings!.defaultDisplayName = value;
                                }}
                                width={TextFieldWidth.auto}
                                disabled={this.disabled}
                                className="flex-grow"
                                containerClassName="flex-grow"
                                inputClassName="flex-grow"
                            />
                        </FormItem>
                    </div>
                ),
                title: Resources.Advanced
            }
        ];
        return (
            <Observer
                isBusy={this.isBusy}
                formErrorMessage={this.formErrorMessage}
                recentStatuses={this.props.recentStatuses}
                policy={this.props.policy}
                isValid={this.isValid}
                renderSeparately={this.renderSeparately}
            >
                {() => {
                    return (
                        <Panel
                            onDismiss={() => this.props.onDismiss()}
                            titleProps={{ text: this.title }}
                            footerButtonProps={[
                                {
                                    text: Resources.Save,
                                    primary: true,
                                    onClick: () => {
                                        this.isBusy.value = true;
                                        this.policy.complexSettings!.statusGenre = this.statusGenre.value;
                                        this.policy.complexSettings!.statusName = this.statusName.value;

                                        if (this.author.value) {
                                            this.props.materializeIdentity(this.author.value).then(author => {
                                                this.policy.complexSettings!.author = author;
                                                this.policy.complexSettings!.authorId = author.localId!;
                                                this.props.save(this.policy, this.handleError);
                                            });
                                        } else {
                                            this.props.save(this.policy, this.handleError);
                                        }
                                    },
                                    disabled: this.disabled || !!this.formErrorMessage.value || !this.isValid.value
                                },
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.props.onDismiss()
                                }
                            ]}
                            description={
                                this.policy.isEnterpriseManaged
                                    ? Resources.ReadOnlyTooltip
                                    : this.policy.isInherited
                                    ? Resources.InheritanceMessageTable
                                    : undefined
                            }
                            overlayContent={this.isBusy.value || !this.props.recentStatuses.value ? <Spinner /> : undefined}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                <Observer failureMessage={this.failureMessage}>
                                    {() => {
                                        return this.failureMessage.value ? (
                                            <MessageCard
                                                onDismiss={() => (this.failureMessage.value = undefined)}
                                                severity={MessageCardSeverity.Error}
                                                className="flex-noshrink"
                                            >
                                                <div className="flex-column font-size-ms">{this.failureMessage.value}</div>
                                            </MessageCard>
                                        ) : null;
                                    }}
                                </Observer>
                                {this.policy.id !== -1 && (
                                    <FormItem label={Resources.Enabled}>
                                        <Toggle
                                            offText={Resources.ToggleOff}
                                            onText={Resources.ToggleOn}
                                            checked={this.isToggled}
                                            onChange={(event, value) => {
                                                this.isToggled.value = value;
                                                this.policy.value = value;
                                            }}
                                            disabled={this.disabled}
                                        />
                                    </FormItem>
                                )}
                                <FormItem label={Resources.StatusToCheck}>
                                    <div className="flex-row flex-center policy-status-field">
                                        <EditableDropdown
                                            allowFreeform={true}
                                            className="icon-margin flex-grow"
                                            items={this.getRecentStatusesItems(this.props.recentStatuses.value || [])}
                                            onValueChange={value => {
                                                this.policy.complexSettings!.status = (value && value.id) || "";
                                                this.selectedStatus.value = this.policy.complexSettings!.status;

                                                if (value?.data?.name) {
                                                    this.statusGenre.value = value?.data?.genre ?? "";
                                                    this.statusName.value = value?.data?.name ?? "";
                                                } else {
                                                    [this.statusName.value, this.statusGenre.value] = this.props.splitStatus(value?.id ?? "");
                                                }
                                            }}
                                            disabled={this.disabled || this.renderSeparately.value}
                                            selectedText={this.selectedStatus}
                                        />
                                        <Icon
                                            ariaLabel={Resources.StatusToCheckTooltip}
                                            iconName="Info"
                                            tooltipProps={{
                                                renderContent: () => renderTooltipWithBreaks(Resources.StatusToCheckTooltip)
                                            }}
                                        />
                                    </div>
                                    <div className="policy-status-bar-check flex-column rhythm-vertical-16 flex-grow margin-8">
                                        <Checkbox
                                            label={Resources.EnterStatusSeparately}
                                            className="policy-status-checkbox-custom-label"
                                            checked={this.renderSeparately}
                                            onChange={(event, checked) => {
                                                this.renderSeparately.value = checked;
                                            }}
                                        />
                                        <div className="flex-row justify-space-between flex-center gap-8">
                                            <label className="bolt-textfield-label">{Resources.StatusGenreField}</label>
                                            <TextField
                                                value={this.statusGenre}
                                                onChange={(event, value) => {
                                                    this.statusGenre.value = value;
                                                    this.policy.complexSettings!.statusGenre = value;
                                                    this.selectedStatus.value = this.statusGenre.value.trim().length > 0 ? (this.statusGenre.value) + '/' + (this.statusName.value ?? "") : this.statusName.value;
                                                }}
                                                width={TextFieldWidth.auto}
                                                disabled={this.disabled || !this.renderSeparately.value}
                                                className="flex-grow"
                                                containerClassName="flex-row flex-grow"
                                                inputClassName="flex-grow"
                                            />
                                            <Icon
                                                ariaLabel={Resources.ManualStatusGenreTooltip}
                                                iconName="Info"
                                                tooltipProps={{
                                                    renderContent: () => renderTooltipWithBreaks(Resources.ManualStatusGenreTooltip)
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row justify-space-between flex-center gap-8">
                                            <label className="bolt-textfield-label">{Resources.StatusNameField}</label>
                                            <TextField
                                                value={this.statusName}
                                                onChange={(event, value) => {
                                                    this.statusName.value = value;
                                                    this.policy.complexSettings!.statusName = this.statusName.value;
                                                    this.selectedStatus.value = this.statusGenre.value.trim().length > 0 ? (this.statusGenre.value) + '/' + (this.statusName.value ?? "") : this.statusName.value
                                                }}
                                                width={TextFieldWidth.auto}
                                                disabled={this.disabled || !this.renderSeparately.value}
                                                className="flex-grow flex-row"
                                                containerClassName="flex-grow"
                                                inputClassName="flex-grow"
                                            />
                                            <Icon
                                                ariaLabel={Resources.ManualStatusNameTooltip}
                                                iconName="Info"
                                                tooltipProps={{
                                                    renderContent: () => renderTooltipWithBreaks(Resources.ManualStatusNameTooltip)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </FormItem>
                                <FormItem label={Resources.PolicyRequirement}>
                                    <RadioButtonGroup
                                        onSelect={id => {
                                            this.selectedPolicyRequirement.value = id;
                                            this.policy.complexSettings!.required = id == requiredId ? true : false;
                                        }}
                                        selectedButtonId={this.selectedPolicyRequirement}
                                        className="flex-start policy-status-radio-button-group"
                                    >
                                        <RadioButton
                                            id={requiredId}
                                            ariaLabelledBy={requiredId + "-rblabel"}
                                            key={requiredId}
                                            disabled={this.disabled}
                                        >
                                            <div
                                                className="policy-status-radio-button-custom-label bolt-radio-button-label"
                                                id={getSafeId(requiredId + "-rblabel")}
                                            >
                                                {Resources.Required}
                                                <span className="body-s secondary-text">{Resources.PolicyRequirementRequiredDescStatus}</span>
                                            </div>
                                        </RadioButton>
                                        <RadioButton
                                            id={optionalId}
                                            ariaLabelledBy={optionalId + "-rblabel"}
                                            key={optionalId}
                                            className="margin-top-16"
                                            disabled={this.disabled}
                                        >
                                            <div
                                                className="policy-status-radio-button-custom-label bolt-radio-button-label"
                                                id={getSafeId(optionalId + "-rblabel")}
                                            >
                                                {Resources.Optional}
                                                <span className="body-s secondary-text">{Resources.PolicyRequirementOptionalDescStatus}</span>
                                            </div>
                                        </RadioButton>
                                    </RadioButtonGroup>
                                </FormItem>
                                <Accordion
                                    sections={accordionSections}
                                    onSectionClicked={(event, section) => {
                                        const collapsed = section.isCollapsed as ObservableValue<boolean>;
                                        collapsed.value = !collapsed.value;
                                    }}
                                />
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    };

    private checkValid = () => {
        const authorValid = this.authorizedOrganization.value == authorizedOrganizationValues.any || !!this.author.value;
        const statusFieldsValid = (this.statusGenre.value.trim().length > 0 && this.statusName.value.trim().length > 0) ||
                                  (this.statusGenre.value.length == 0 && this.statusName.value.trim().length > 0);
        this.isValid.value = !!this.selectedStatus.value && this.selectedStatus.value.length > 0 && authorValid && statusFieldsValid;
    };

    private handleError = (error: any) => {
        this.isBusy.value = false;
        this.failureMessage.value = error.toString();
    };

    private isCollapsedByDefault = () => {
        return this.policy.id === -1;
    };

    private getRecentStatusesItems = (recentStatuses: GitStatusContext[]): IListBoxItem<GitStatusContext>[] => {
        return recentStatuses.map(status => {
            const id = (status.genre ? status.genre + '/' : '') + status.name;
            return {
                id: id,
                text: id,
                data: status
            } as IListBoxItem<GitStatusContext>;
        })
    };
}
